import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./components.css";
import { get } from "../services/ApiService";
import { usePermissions } from "../redux/PermissionsContext";
import { useChatCounts } from "../context/ChatCountsContext";
import { post } from "../services/ApiService";
import toast, { Toaster } from "react-hot-toast";
import Notifications from "./Notifications";
import { ESCALATION_TYPES, ESCALATION_TYPE_KEYS } from "../pages/Orders/Escalations/escalationTypes";

const Sidebar = () => {
  const { hasPermission } = usePermissions();
  const { counts: chatCounts } = useChatCounts();
  const chatBadgeCount = chatCounts?.OpenTicket || 0;
  const [activeItem, setActiveItem] = useState("");
  const [orderCount, setOrderCount] = useState(0);
  const [repeatOffenderCount, setRepeatOffenderCount] = useState(0);
  const [caseCount, setCaseCount] = useState(0);
  const [escalationCounts, setEscalationCounts] = useState({});
  const [scheduledDispatchCount, setScheduledDispatchCount] = useState(0);
  const { pathname } = useLocation();
  const canViewScheduledDispatch = hasPermission(["order.edit"]);
  const escalationTypes = ESCALATION_TYPE_KEYS.filter((type) => hasPermission([ESCALATION_TYPES[type].viewPermission]));
  const escalationTypesKey = escalationTypes.join(",");

  useEffect(() => {
    setActiveItem(pathname);
    getInquiryCount();
    getPendingOrderCount();
    getRepeatOffenderCount();
    escalationTypes.forEach(getEscalationCount);
    if (canViewScheduledDispatch) getScheduledDispatchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, escalationTypesKey, canViewScheduledDispatch]);

  const getEscalationCount = async (type) => {
    try {
      const res = await get(ESCALATION_TYPES[type].api.count);
      setEscalationCounts((counts) => ({ ...counts, [type]: res?.count || 0 }));
    } catch (error) {
      console.error("Error fetching escalation count:", error);
    }
  };

  const getPendingOrderCount = async () => {
    try {
      const res = await get("api/orders/pending/count");
      if (res.success) {
        setOrderCount(Number(res.order) || 0);
      }
    } catch (error) {
      console.error("Error fetching pending order count:", error);
    }
  };

  const getScheduledDispatchCount = async () => {
    try {
      const res = await get("api/orders/scheduled-dispatch/count");
      if (res.success) {
        setScheduledDispatchCount(Number(res.count) || 0);
      }
    } catch (error) {
      console.error("Error fetching scheduled dispatch count:", error);
    }
  };

  const getRepeatOffenderCount = async () => {
    try {
      const res = await get("api/orders/repeat-offenders/count");
      if (res.success) {
        setRepeatOffenderCount(Number(res.count) || 0);
      }
    } catch (error) {
      console.error("Error fetching repeat offender count:", error);
    }
  };

  const canViewPendingOrders = hasPermission(["order.pending_p_approval"]);
  const canViewRepeatOffenders = hasPermission(["order.assessment_incomplete"]);
  const canViewPharmacyEscalations = hasPermission([ESCALATION_TYPES.PHARMACY.viewPermission]);
  const pharmacistQueueTotal =
    (canViewPendingOrders ? Number(orderCount) || 0 : 0) +
    (canViewRepeatOffenders ? Number(repeatOffenderCount) || 0 : 0) +
    (canViewPharmacyEscalations ? Number(escalationCounts.PHARMACY) || 0 : 0);

  function isActivePharmacistQueue(path) {
    return (
      path === "/orders/pharma-medicine" ||
      path === "/orders/repeat-offenders" ||
      path.startsWith("/pharmacy-escalations")
    );
  }

  function isActiveOrder(path) {
    return (
      path === "/orders" ||
      path === "/orders/assessment-required" ||
      path.startsWith("/orders/edit/") ||
      path.startsWith("/order/assessment/") ||
      (path.startsWith("/orders/") &&
        path !== "/orders/pharma-medicine" &&
        path !== "/orders/repeat-offenders" &&
        path !== "/orders/report" &&
        !path.startsWith("/orders/courier-jobs"))
    );
  }
  function isActiveProduct(path) {
    return (
      path === "/products" ||
      path === "/variations" ||
      path.startsWith("/products/assign-question/") ||
      path.startsWith("/products/edit/")
    );
  }
  function isActiveProductList(path) {
    return (
      path === "/products" ||
      path.startsWith("/products/assign-question/") ||
      path.startsWith("/products/edit/")
    );
  }

  function isActiveOrdersList(path) {
    if (path === "/orders" || path.startsWith("/orders/edit/")) return true;
    if (
      path === "/orders/pharma-medicine" ||
      path === "/orders/assessment-required" ||
      path === "/orders/repeat-offenders" ||
      path === "/orders/report" ||
      path.startsWith("/orders/courier-jobs")
    ) {
      return false;
    }
    return path.startsWith("/orders/");
  }

  function isActiveLogs(path) {
    return (
      path === "/near-miss-logs" ||
      path.startsWith("/near-miss-logs/add") ||
      path.startsWith("/incident-logs") ||
      path.startsWith("/file-manager")
    );
  }

  function isActiveLogsList(path) {
    return (
      path === "/near-miss-logs" ||
      path.startsWith(
        "/near-miss-logs/add" || path.startsWith("/incident-logs")
      )
    );
  }

  function isActiveIncidentList(path) {
    return path === "/incident-logs" || path.startsWith("/incident-logs");
  }

  const getInquiryCount = async () => {
    try {
      const res = await get(`api/inquiries/count`);
      setCaseCount(res?.count || 0);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  };
  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="db-sidebar bg-body">
        <aside className="navbar navbar-expand-xl navbar-light d-block px-0 header-sticky dashboard-nav py-0">
          <div className="sticky-area border-right">
            <div className="d-flex px-6 px-xl-10 w-100 border-bottom py-7 justify-content-between">
              <a href="/" className="navbar-brand py-4">
                <img
                  className="light-mode-img invert_logo"
                  src="/assets/images/logo.svg"
                  width={150}
                  height={26}
                />
                {/* <img className="dark-mode-img" src="/assets/images/logo.svg" width={100} height={26} /> */}
              </a>
              <div className="ml-auto d-flex align-items-center">
                <button
                  className="navbar-toggler border-0 px-0"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#primaryMenuSidebar"
                  aria-controls="primaryMenuSidebar"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                >
                  <span className="navbar-toggler-icon" />
                </button>
              </div>
            </div>
            {hasPermission(["general.notifications"]) && (
            <div className="col-12 d-xl-none border-bottom">
              <div className="d-flex align-items-center justify-content-center py-6">
                <Notifications />
              </div>
            </div>
            )}
            <div
              className="collapse navbar-collapse bg-body z-index-5"
              id="primaryMenuSidebar"
            >
              <form className="d-block d-xl-none pt-8 px-6">
                <div className="input-group position-relative bg-body-tertiary">
                  <input
                    type="text"
                    className="form-control border-0 bg-transparent pl-4 shadow-none"
                    placeholder="Search Item"
                  />
                  <div className="input-group-append fs-14px px-6 border-start border-2x">
                    <button className="bg-transparent border-0 outline-none py-5">
                      <i className="far fa-search" />
                    </button>
                  </div>
                </div>
              </form>
              <ul className="list-group list-group-flush list-group-no-border w-100 p-6">
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem === "/" ? "active" : ""
                    }`}
                >
                  <a
                    href="/"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Dashboard"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-home-lg-alt" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Dashboard
                    </span>
                  </a>
                </li>
                {/* Users + Roles sidebar entries removed: user/role management moved to inventory admin UI. */}
                {hasPermission(["product.view"]) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#product"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Products"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-shopping-bag" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Products
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${isActiveProduct(activeItem) ? "show" : ""
                        }`}
                      id="product"
                    >
                      <ul className="sub-menu list-unstyled">
                        <li
                          className={`sidebar-item ${isActiveProductList(activeItem) ? "active" : ""
                            }`}
                        >
                          <a
                            href="/products"
                            className={`sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold`}
                            title="Product List"
                          >
                            Product List
                          </a>
                        </li>
                        {hasPermission(["product.variations"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/postages" ? "active" : ""
                              }`}
                          >
                            <a
                              href="/postages"
                              className={`sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold `}
                              title="Postages List"
                            >
                              Postages
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}
                {(hasPermission(["question.view"]) || hasPermission(["question.view_templates"])) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#question"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Products"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-comment-alt" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Questions
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${activeItem === "/questions" ||
                        activeItem === "/questions/add" ||
                        activeItem === "/questions/edit" ||
                        activeItem?.startsWith("/assessment-templates")
                        ? "show"
                        : ""
                        }`}
                      id="question"
                    >
                      <ul className="sub-menu list-unstyled">
                        {hasPermission(["question.view"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/questions" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/questions`}
                              title="Product List"
                            >
                              Question List
                            </a>
                          </li>
                        )}
                        {hasPermission(["question.create"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/questions/add" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/questions/add`}
                              title="Product List"
                            >
                              Add Question
                            </a>
                          </li>
                        )}
                        {hasPermission(["question.view_templates"]) && (
                          <li
                            className={`sidebar-item ${activeItem?.startsWith("/assessment-templates") ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href="/assessment-templates"
                              title="Assessment templates"
                            >
                              Templates
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}
                {(canViewPendingOrders || canViewRepeatOffenders || canViewPharmacyEscalations) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#pharmacist-queue"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 ps-6 pe-10 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Pharmacy Actions"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-clipboard-list" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Pharmacy Actions{" "}
                        <span className="badge bg-primary">{pharmacistQueueTotal}</span>
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${isActivePharmacistQueue(activeItem) ? "show" : ""
                        }`}
                      id="pharmacist-queue"
                    >
                      <ul className="sub-menu list-unstyled">
                        {canViewPendingOrders && (
                          <li
                            className={`sidebar-item ${activeItem === "/orders/pharma-medicine"
                              ? "active"
                              : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href="/orders/pharma-medicine"
                              title="Pending P Approval"
                            >
                              Pending P Approval{" "}
                              <span className="badge bg-primary">{orderCount}</span>
                            </a>
                          </li>
                        )}
                        {canViewRepeatOffenders && (
                          <li
                            className={`sidebar-item ${activeItem === "/orders/repeat-offenders"
                              ? "active"
                              : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href="/orders/repeat-offenders"
                              title="Repeat Offenders"
                            >
                              Repeat Offenders{" "}
                              <span className="badge bg-primary">{repeatOffenderCount}</span>
                            </a>
                          </li>
                        )}
                        {canViewPharmacyEscalations && (
                          <li
                            className={`sidebar-item ${activeItem?.startsWith("/pharmacy-escalations") ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href="/pharmacy-escalations"
                              title={ESCALATION_TYPES.PHARMACY.title}
                            >
                              {ESCALATION_TYPES.PHARMACY.sidebarLabel}{" "}
                              <span className="badge bg-primary">{escalationCounts.PHARMACY || 0}</span>
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}
                {hasPermission(["order.management"]) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#order"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Order"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-shopping-cart" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Order
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${isActiveOrder(activeItem) ? "show" : ""
                        }`}
                      id="order"
                    >
                      <ul className="sub-menu list-unstyled">
                        {hasPermission(["order.all_orders"]) && (
                          <li
                            className={`sidebar-item ${isActiveOrdersList(activeItem) ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/orders`}
                              title="Order List 1"
                            >
                              All Orders
                            </a>
                          </li>
                        )}
                        {hasPermission(["order.assessment_incomplete"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/orders/assessment-required"
                              ? "active"
                              : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/orders/assessment-required`}
                              title="Assessment Incomplete"
                            >
                              Assessment Incomplete
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}
                {hasPermission(["setting.events"]) && (
                  <li
                    className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem === "/events" ? "active" : ""
                      }`}
                  >
                    <a
                      href="/events"
                      className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                      title="Dashboard"
                    >
                      <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                        <i className="fas fa-user" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Events
                      </span>
                    </a>
                  </li>
                )}
                {/* {hasPermission(['setting.site_setting']) && 
                <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                  <a href="#setting" className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative" data-bs-toggle="collapse" aria-expanded="false" title="Setting">
                    <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                      <i className="fas fa-cog" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">Setting</span>
                    <span className="position-absolute top-50 end-5 translate-middle-y"><i className="far fa-angle-down" /></span>
                  </a>
                  <div className={`collapse menu-collapse ${activeItem === '/site-setting' || activeItem === '/setting' ? 'show' : ''}`} id="setting">
                    <ul className="sub-menu list-unstyled">
                      <li className="sidebar-item">
                        <a className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold" href="../dashboard/profile-settings.html" title="Profile settings">Profile settings</a>
                      </li>
                      {hasPermission(['setting.site_setting']) &&
                      <li className= {`sidebar-item ${activeItem === '/events'? 'active' : ''}`}>
                        <a className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold" href="/Events" title="Site settings">Events</a>
                      </li>
                      }
                    </ul>
                  </div>
                </li>
                } */}
                {hasPermission(["external_log.near_miss_view"]) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#log-sec"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Logs"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-history" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        External Logs
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${isActiveLogs(activeItem) ? "show" : ""
                        }`}
                      id="log-sec"
                    >
                      <ul className="sub-menu list-unstyled">
                        <li
                          className={`sidebar-item ${isActiveLogsList(activeItem) ? "active" : ""
                            }`}
                        >
                          <a
                            className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                            href={`/near-miss-logs`}
                            title="Order List 1"
                          >
                            Near Miss Error
                          </a>
                        </li>
                        <li
                          className={`sidebar-item ${isActiveIncidentList(activeItem) ? "active" : ""
                            }`}
                        >
                          <a
                            className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                            href={`/incident-logs`}
                            title="incident-logs"
                          >
                            Incident Logs
                          </a>
                        </li>
                        {hasPermission(["external_log.file_manager"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/file-manager" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/file-manager`}
                              title="Order List 1"
                            >
                              File Manager
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}
                {hasPermission(["meeting.view"]) && (
                  <li className="list-group-item px-0 py-0 sidebar-item mb-3 has-children border-0">
                    <a
                      href="#meeting-sec"
                      className="text-heading text-decoration-none lh-1 d-flex sidebar-link align-items-center py-5 px-6 position-relative"
                      data-bs-toggle="collapse"
                      aria-expanded="false"
                      title="Logs"
                    >
                      <span className="sidebar-item-icon d-inline-block w-40px text-muted">
                        <i className="fas fa-calendar" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Meeting
                      </span>
                      <span className="position-absolute top-50 end-5 translate-middle-y">
                        <i className="far fa-angle-down" />
                      </span>
                    </a>
                    <div
                      className={`collapse menu-collapse ${activeItem === "/meetings" ||
                        activeItem === "/meetings/add" ||
                        activeItem === "/meetings/staff" ||
                        activeItem === "/notes"
                        ? "show"
                        : ""
                        }`}
                      id="meeting-sec"
                    >
                      <ul className="sub-menu list-unstyled">
                        {hasPermission(["meeting.management_meeting"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/meetings" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/meetings`}
                              title="Order List 1"
                            >
                              Management Meetings
                            </a>
                          </li>
                        )}
                        {hasPermission(["meeting.staff_meeting"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/meetings/staff" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/meetings/staff`}
                              title="Order List 1"
                            >
                              Staff Meetings
                            </a>
                          </li>
                        )}
                        {hasPermission(["general.notes"]) && (
                          <li
                            className={`sidebar-item ${activeItem === "/notes" ? "active" : ""
                              }`}
                          >
                            <a
                              className="sidebar-link pe-5 ps-8 py-5 lh-1 text-decoration-none fs-14px fw-semibold"
                              href={`/notes`}
                              title="notes"
                            >
                              Notes
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </li>
                )}

                {hasPermission(["general.case_view"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/cases") ? "active" : ""
                    } `}
                >
                  <a
                    href={`/cases`}
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Dashboard"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fa fa-folder" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Case <span className="badge bg-primary">{caseCount}</span>
                    </span>
                  </a>
                </li>
                )}

                {escalationTypes.filter((type) => type !== "PHARMACY").map((type) => {
                  const { path, title, sidebarIcon, sidebarLabel } = ESCALATION_TYPES[type];
                  return (
                <li
                  key={type}
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith(path) ? "active" : ""
                    } `}
                >
                  <a
                    href={path}
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title={title}
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className={sidebarIcon} />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      {sidebarLabel} <span className="badge bg-primary">{escalationCounts[type] || 0}</span>
                    </span>
                  </a>
                </li>
                  );
                })}

                {canViewScheduledDispatch && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/scheduled-dispatch") ? "active" : ""
                    } `}
                >
                  <a
                    href="/scheduled-dispatch"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Scheduled Dispatch"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-clock" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Scheduled Dispatch <span className="badge bg-primary">{scheduledDispatchCount}</span>
                    </span>
                  </a>
                </li>
                )}

                {hasPermission(["order.tracker"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/tracking") ? "active" : ""
                    } `}
                >
                  <a
                    href={`/tracking`}
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Royal Mail Tracking"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-shipping-fast" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Tracking
                    </span>
                  </a>
                </li>
                )}
                {hasPermission(["order.return_create"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/returns") ? "active" : ""
                    } `}
                >
                  <a
                    href={`/returns`}
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="QR Returns"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-undo" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Returns
                    </span>
                  </a>
                </li>
                )}
                {hasPermission(["order.view_message"]) && (
                  <li
                    className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem === "/chat" ? "active" : ""} `}
                  >
                    <a
                      href={`/chat`}
                      className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                      title="Chat"
                    >
                      <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                        <i className="fa fa-comments" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Chat {chatBadgeCount > 0 && <span className="badge bg-primary">{chatBadgeCount}</span>}
                      </span>
                    </a>
                  </li>
                )}

                {hasPermission(["order.chat_report"]) && (
                  <li
                    className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/chat/report") ? "active" : ""} `}
                  >
                    <a
                      href={`/chat/report`}
                      className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                      title="Chat Report"
                    >
                      <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                        <i className="fa fa-bar-chart" />
                      </span>
                      <span className="sidebar-item-text fs-14px fw-semibold">
                        Chat Report
                      </span>
                    </a>
                  </li>
                )}

                {hasPermission(["general.complaints_view"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${
                    activeItem?.startsWith("/complaints") ? "active" : ""
                  } `}
                >
                  <a
                    href="/complaints"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Complaints"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fa fa-exclamation-triangle" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Complaints
                    </span>
                  </a>
                </li>
                )}

                {hasPermission(["general.inventory"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 `}
                >
                  <a
                    href="https://inventory.thestgroup.co.uk"
                    target="_blank"
                    rel="noreferrer"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Dashboard"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fa fa-bar-chart" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Inventory
                    </span>
                  </a>
                </li>
                )}

                {hasPermission(["order.courier_jobs"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem?.startsWith("/orders/courier-jobs") ? "active" : ""}`}
                >
                  <a
                    href="/orders/courier-jobs"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Courier Jobs"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-truck" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Courier Jobs
                    </span>
                  </a>
                </li>
                )}

                {hasPermission(["order.pgd_catd_report"]) && (
                <li
                  className={`list-group-item px-0 py-0 sidebar-item mb-3 border-0 ${activeItem === "/orders/report" ? "active" : ""}`}
                >
                  <a
                    href="/orders/report"
                    className="text-heading text-decoration-none lh-1 sidebar-link py-5 px-6 d-flex align-items-center"
                    title="Reports"
                  >
                    <span className="sidebar-item-icon w-40px d-inline-block text-muted">
                      <i className="fas fa-file-alt" />
                    </span>
                    <span className="sidebar-item-text fs-14px fw-semibold">
                      Reports
                    </span>
                  </a>
                </li>
                )}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Sidebar;
